import React, { useEffect, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, Box, Text, Input, useToast,
} from '@chakra-ui/react';

const CURSIVE = '"Segoe Script","Bradley Hand","Snell Roundhand",cursive';

export default function SignAndSaveModal({
  isOpen, onClose, mode = 'sign', defaultSignature = '', password = '',
  signatureText = '', signerName = '', isBusy = false, onConfirmSign,
}) {
  const [text, setText] = useState(defaultSignature);
  const toast = useToast();

  // Reset the field whenever the modal (re)opens in sign mode.
  useEffect(() => {
    if (isOpen && mode === 'sign') setText(defaultSignature);
  }, [isOpen, mode, defaultSignature]);

  const trimmed = text.trim();

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      toast({ title: 'Password copied', status: 'success', duration: 1500 });
    } catch {
      toast({ title: 'Copy failed — select and copy manually', status: 'warning', duration: 2500 });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        {mode === 'sign' ? (
          <>
            <ModalHeader>Sign &amp; Lock</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text fontSize="sm" mb={2}>
                Signing records your signature, locks this note, and generates a
                password. Share the password with anyone who needs to edit it later.
              </Text>
              <Text as="label" htmlFor="signature-input" fontSize="sm" fontWeight="semibold">
                Type your signature
              </Text>
              <Input
                id="signature-input"
                aria-label="Type your signature"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Dr. Jane Lee"
                mt={1}
              />
              <Box mt={3} p={3} borderWidth="1px" borderRadius="md" bg="gray.50">
                <Text fontSize="xs" color="gray.500" mb={1}>Signature preview</Text>
                <Text fontSize="2xl" style={{ fontFamily: CURSIVE }} minH="1.5em">
                  {trimmed}
                </Text>
              </Box>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
              <Button
                colorScheme="green"
                isLoading={isBusy}
                isDisabled={!trimmed || isBusy}
                onClick={() => onConfirmSign?.(trimmed)}
              >
                Sign &amp; Lock
              </Button>
            </ModalFooter>
          </>
        ) : (
          <>
            <ModalHeader>Note signed &amp; locked</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Box mb={3}>
                <Text fontSize="xs" color="gray.500">Signed by {signerName}</Text>
                <Text fontSize="2xl" style={{ fontFamily: CURSIVE }}>{signatureText}</Text>
              </Box>
              <Text fontSize="sm" fontWeight="semibold" mb={1}>Edit password</Text>
              <Box p={3} borderWidth="1px" borderRadius="md" fontFamily="mono" fontSize="xl" textAlign="center" letterSpacing="wider">
                {password}
              </Box>
              <Text fontSize="xs" color="gray.500" mt={2}>
                Anyone who wants to edit this note from now on must enter this password.
                Only you can re-open this to view it again.
              </Text>
            </ModalBody>
            <ModalFooter>
              <Button variant="outline" mr={3} onClick={copy}>Copy</Button>
              <Button colorScheme="blue" onClick={onClose}>Done</Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
